import axios from 'axios';
import _ from 'lodash';
import { credencialExpirada, guardarCredencial } from './credencialPublica';

/*
 * Sequência das requisições autenticadas: cada uma pega o próximo número
 * antes de disparar (fetch/create/createFile/update/remove). As respostas
 * podem chegar fora de ordem — por exemplo, uma renovação de credencial (sem
 * corpo) que demora mais que o /salvar disparado logo depois — e sem isso a
 * renovação atrasada sobrescreve a credencial nova (já com o id da inscrição
 * recém-criada) pela antiga, e as chamadas seguintes (/atualizar, /ficha)
 * voltam 403. Só quem tem a maior sequência já vista pode gravar.
 */
let sequencia = 0;
let ultimaSequenciaGuardada = 0;

/* Regra pura da ordenação, extraída para poder testar sem montar o axios. */
export const deveGuardarCredencial = (seq, ultimaGuardada) => seq > ultimaGuardada;

/* O back devolve a credencial renovada (ou nova) no header da resposta. */
const _renovarCredencial = (response, seq) => {
    const credencial = response && response.headers && response.headers['credencial'];
    if (credencial && deveGuardarCredencial(seq, ultimaSequenciaGuardada)) {
        ultimaSequenciaGuardada = seq;
        guardarCredencial(credencial);
    }
    return response;
};

/* 401: a credencial não vale mais (vencida/adulterada) — volta para a identificação. */
const _verificarCredencial = error => {
    if (error && error.response && error.response.status === 401) {
        credencialExpirada(error.response.data);
    }
    return error;
};

/* Monta os query params: arrays viram vários append da mesma chave. */
const _montarUrlParams = params => {
    const urlParams = new URLSearchParams();

    if (params) {
        for (let key in params) {
            const paramValue = params[key];

            if (Array.isArray(paramValue)) {
                paramValue.forEach(value => urlParams.append(key, value));
            } else {
                urlParams.append(key, paramValue);
            }
        }
    }

    return urlParams;
};

export const fetch = (url, credencial, params,  config) => {
    const urlParams = _montarUrlParams(params);

    const seq = ++sequencia;
    return axios.get(url, {
        ...config,
        headers: _.assign({}, config?.headers, { 'credencial': credencial }),
        params: urlParams
    }).then(response => _renovarCredencial(response, seq)).then(response => {
        return response.data;
    }).catch(_handleErrorResponse);
};

export const create = (url, credencial, data, config) => {
    const seq = ++sequencia;
    return axios.post(url, data, {
        ...config,
        headers: _.assign({}, config?.headers, { 'credencial': credencial })
    }).then(response => _renovarCredencial(response, seq)).then(response => {
        return response.data;
    }).catch(_handleErrorResponse);
};

export const createFile = (url, credencial, data, config) => {
    const seq = ++sequencia;
    return axios.post(url, data, {
        ...config,
        responseType: 'blob',
        headers: _.assign({}, config?.headers, { 'credencial': credencial })
    }).then(response => _renovarCredencial(response, seq))
        .catch(_handleFileErrorResponse);
};

/* GET que baixa arquivo (blob): irmão do createFile, devolve a resposta inteira. */
export const fetchFile = (url, credencial, params, config) => {
    const urlParams = _montarUrlParams(params);

    const seq = ++sequencia;
    return axios.get(url, {
        ...config,
        responseType: 'blob',
        headers: _.assign({}, config?.headers, { 'credencial': credencial }),
        params: urlParams
    }).then(response => _renovarCredencial(response, seq))
        .catch(_handleFileErrorResponse);
};

export const update = (url, credencial, data, config) => {
    const seq = ++sequencia;
    return axios.put(url, data, {
        ...config,
        headers: _.assign({}, config?.headers, { 'credencial': credencial })
    }).then(response => _renovarCredencial(response, seq)).then(response => {
        return response.data;
    }).catch(_handleErrorResponse);
};

export const remove = (url, credencial, data) => {
    const seq = ++sequencia;
    return axios.delete(url, {
        headers: {
            'credencial': credencial
        },
        data
    }).then(response => _renovarCredencial(response, seq)).then(response => {
        return response.data;
    }).catch(_handleErrorResponse);
};

const _handleErrorResponse = error => {
    _verificarCredencial(error);

    if (error.response && error.response.data) {
        return Promise.reject(error.response.data);
    }

    return Promise.reject(error.message); // Mesmo efeito: 'throw error.message'
};

const _handleFileErrorResponse = error => {
    _verificarCredencial(error);

    const responseData = error.response && error.response.data;
    if (responseData && typeof responseData.text === 'function') {
        return responseData.text().then(text => {
            try {
                return Promise.reject(JSON.parse(text));
            } catch (parseError) {
                return Promise.reject(text || error.message);
            }
        });
    }

    return _handleErrorResponse(error);
};

const _onUploadProgress = progressEvent => {
    Math.round((progressEvent.loaded * 100) / progressEvent.total);
};
